import { changetheme, changemenucolor } from '@/store/layout/layout-action';
import type { LayoutState } from '@/store/layout/layout-reducers';
import { getLayoutColor, getMenucolor } from '@/store/layout/layout-selector';
import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter,
  HostListener,
  inject,
  Input,
  Output,
  type OnInit,
  type TemplateRef,
} from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import {
  NgbDropdownModule,
  NgbModal,
  NgbModalModule,
  NgbModalRef,
  NgbOffcanvasModule,
} from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { SimplebarAngularModule } from 'simplebar-angular';
import { splitArray } from '@core/helper/utils';
import { LogoBoxComponent } from '@components/logo-box.component';
import { currency } from '@common/constants';
import { logout } from '@/store/authentication/authentication.actions';
import { appData, languages } from './data';
import { TranslatePipe } from '@ngx-translate/core';
import { EmployeeActions } from '@/store/employee/employee.actions';
import { AuthenticationService } from '@core/services/auth.service';
import { EmployeeProfile, ROLES } from '@core/interface/employee';
import { ChangePasswordComponent } from '@components/change-password/change-password.component';
import { debounce } from 'lodash';
import { DocumentActions } from '@/store/employee/document/document.actions';
import { getDocumentData } from '@/store/employee/document/document.selectors';
import { DocumentItem } from '@core/interface/employee-document';
import { DocumentComponent } from '@views/pages/employess/document/document.component';
import { BehaviorSubject, Subject } from 'rxjs';
import { ChangeCompanyComponent } from '@components/change-company/change-company.component';
import { NotificationCenterComponent } from '@components/notification-center/notification-center.component';

@Component({
  selector: 'app-topbar',
  standalone: true,
  imports: [
    NgbOffcanvasModule,
    NgbDropdownModule,
    SimplebarAngularModule,
    NgbModalModule,
    LogoBoxComponent,
    TranslatePipe,
    NotificationCenterComponent,
  ],
  templateUrl: './topbar.component.html',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  styles: ``,
})
export class TopbarComponent implements OnInit {
  private authService = inject(AuthenticationService);
  currency = currency;
  languageList = languages;
  appsChunks = splitArray(appData, 3);
  @Input() title!: string;
  @Input() subTitle!: string;
  @Output() settingsButtonClicked = new EventEmitter();
  @Output() mobileMenuButtonClicked = new EventEmitter();
  scrollPosition: number = 0;
  documents$: Subject<DocumentItem[]> = new Subject<DocumentItem[]>();
  private modalService = inject(NgbModal);
  router = inject(Router);
  store = inject(Store);
  color!: string;
  user: EmployeeProfile | null = null;
  ROLES = ROLES;
  constructor() {
    this.onScroll = debounce(this.onScroll, 100); // Debounce scroll handler
  }

  @HostListener('window:scroll', [])
  onScroll = (): void => {
    this.scrollPosition = window.scrollY;
    const topbar = document.getElementById('header');
    if (topbar) {
      if (this.scrollPosition >= 25) {
        topbar.classList.add('topbar-active');
      } else {
        topbar.classList.remove('topbar-active');
      }
    }
  };

  open(content: TemplateRef<any>) {
    this.modalService.open(content, { size: 'lg' });
  }

  ngOnInit(): void {
    this.authService.getUser().subscribe((user) => {
      if (user) {
        this.user = user;
      }
    });
    this.store.select('layout').subscribe((data: LayoutState) => {
      this.color = data.LAYOUT_THEME;
    });
    this.store.select(getDocumentData).subscribe((data) => {
      this.documents$.next(data.documents);
    });
  }

  ngOnDestroy(): void {}

  settingMenu() {
    this.settingsButtonClicked.emit();
  }

  toggleMobileMenu() {
    this.mobileMenuButtonClicked.emit();
  }

  changeTheme() {
    const color = document.documentElement.getAttribute('data-bs-theme');
    let setColor: string = '';
    if (color == 'light') {
      setColor = 'dark';
    } else {
      setColor = 'light';
    }
    this.store.dispatch(changetheme({ color: setColor }));
    this.changeMenu(setColor);
    this.store.select(getLayoutColor).subscribe((color) => {
      document.documentElement.setAttribute('data-bs-theme', color);
    });
  }

  // Change Menu Color
  changeMenu(menu: any) {
    this.store.dispatch(changemenucolor({ menu }));
    this.store.select(getMenucolor).subscribe((menucolor) => {
      document.documentElement.setAttribute('data-menu-color', menucolor);
    });
  }

  logout() {
    this.store.dispatch(EmployeeActions.employeesLogout());
    this.store.dispatch(logout());
  }

  changePassword() {
    this.modalService.dismissAll();
    this.modalService.open(ChangePasswordComponent, { size: 'lg' });
  }

  viewDocuments() {
    if (this.user) {
      this.store.dispatch(
        DocumentActions.loadDocuments({
          user_id: this.user.id,
        })
      );
    }
    this.modalService.dismissAll();
    const modalRef = this.modalService.open(DocumentComponent, {
      size: 'lg',
    });
    modalRef.componentInstance.isViewMode = true;
    modalRef.componentInstance.documents$ = this.documents$;
    modalRef.componentInstance.userId = this.user?.id;
    modalRef.dismissed.subscribe(() => {
      this.store.dispatch(DocumentActions.resetDocuments());
    });
  }

  changeCompany() {
    this.modalService.dismissAll();
    const modalRef = this.modalService.open(ChangeCompanyComponent, {
      size: 'lg',
    });
    modalRef.componentInstance.currentCompany = this.user?.default_company_id;
    modalRef.componentInstance.userId = this.user?.id;
    modalRef.result.then((res) => {
      window.location.reload();
    });
  }
}
